import { useMemo } from "react";

import { useFlags } from "launchdarkly-react-client-sdk";
import { orderBy, sortBy } from "lodash";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom-v5-compat";

import useGetServiceTeams from "@fartherfinance/frontend/api/Entity/hooks/useGetServiceTeams";
import {
  taskStatusTypeMapV2,
  taskTypeStatusMapV2,
} from "@fartherfinance/frontend/api/Tasks/constants/options";
import useGetTaskAdvisors from "@fartherfinance/frontend/api/Tasks/hooks/persons/useGetTaskAdvisors";
import useGetTaskAssignees from "@fartherfinance/frontend/api/Tasks/hooks/persons/useGetTaskAssignees";
import useGetTaskClients from "@fartherfinance/frontend/api/Tasks/hooks/persons/useGetTaskClients";
import { GetAssigneesQueryParams } from "@fartherfinance/frontend/api/Tasks/requests/persons/getAssigneesV2";
import {
  AssigneeType,
  TaskDetailsV2,
  TaskDetailsV3,
  TaskStatus,
  TaskType,
  TaskTypeV2,
} from "@fartherfinance/frontend/api/Tasks/Types";
import { isGroupAssignee } from "@fartherfinance/frontend/api/Tasks/utils/isGroupAssignee";
import {
  mapTaskStatusToLabel,
  mapTaskTypeToLabelV2,
} from "@fartherfinance/frontend/api/Tasks/utils/mappings";
import { TaskId } from "@fartherfinance/frontend/api/Types";

import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import { mapPersonToOption } from "@src/multiCustodian/pages/Advisor/utils";

import { mapAssigneeToOption, mapSerivceTeamToOption } from "./utils";

export type NavigateTaskDetailsState = {
  from: "CREATE_SUBTASK";
};

export const useTasksNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigateToTasksList = (): void => {
    navigate({
      pathname: "/Advisor/Tasks",
      search: location.search,
    });
  };

  const handleNavigateToTaskDetails = (
    taskId: TaskId,
    state?: NavigateTaskDetailsState
  ): void => {
    navigate(
      {
        pathname: `/Advisor/Tasks/${taskId}`,
        search: location.search,
      },
      { state }
    );
  };

  const handleNavigateToTaskCreation = (): void => {
    navigate({
      pathname: "/Advisor/Tasks/CreateTask",
      search: location.search,
    });
  };

  const handleNavigateToSubtaskCreation = (
    taskDetails: TaskDetailsV2 | TaskDetailsV3
  ): void => {
    navigate(
      {
        pathname: "/Advisor/Tasks/CreateSubtask",
        search: location.search,
      },
      { state: taskDetails }
    );
  };

  return {
    handleNavigateToTasksList,
    handleNavigateToTaskDetails,
    handleNavigateToTaskCreation,
    handleNavigateToSubtaskCreation,
  };
};

export const useTaskStatusOptionsV2 = (taskType?: TaskTypeV2) => {
  const { enableTaskCanceledStatus } = useFlags();

  const taskStatuses = useMemo(() => {
    return taskType
      ? taskTypeStatusMapV2[taskType]
      : taskTypeStatusMapV2["all"];
  }, [taskType]);

  const taskStatusOptions = useMemo(
    () =>
      enableTaskCanceledStatus
        ? taskStatuses
        : taskStatuses.filter(
            (taskStatus) => taskStatus !== TaskStatus.enum.CANCELED
          ),
    [taskStatuses, enableTaskCanceledStatus]
  );

  return {
    taskStatusOptions,
    taskStatusFormOptions: taskStatusOptions.map((taskStatus) => ({
      label: mapTaskStatusToLabel(taskStatus),
      value: taskStatus,
      meta: {
        group:
          taskStatus === TaskStatus.enum.CANCELED ||
          taskStatus === TaskStatus.enum.COMPLETED
            ? "Closed"
            : "Active",
      },
    })),
  };
};

export const useTaskTypeOptionsV2 = (
  taskStatus?: TaskStatus,
  isSystemGenerated?: boolean
) => {
  const { enableTaskServiceTeams } = useFlags();
  const isAdvisor = !enableTaskServiceTeams;

  const taskTypes = useMemo(() => {
    return taskStatus
      ? taskStatusTypeMapV2[taskStatus]
      : taskStatusTypeMapV2["all"];
  }, [taskStatus]);

  const taskTypeOptions = useMemo(() => {
    let filteredTypes = taskTypes;

    // Filter out LIQUIDATION for non-system generated tasks
    if (!isSystemGenerated) {
      filteredTypes = filteredTypes.filter(
        (taskType) => taskType !== TaskType.enum.LIQUIDATION
      );
    }

    // Filter out IRA_ROLLOVERS for advisors
    if (isAdvisor) {
      filteredTypes = filteredTypes.filter(
        (taskType) => taskType !== TaskTypeV2.enum.IRA_ROLLOVERS
      );
    }

    return filteredTypes;
  }, [taskTypes, isSystemGenerated, isAdvisor]);

  return {
    taskTypeOptions,
    taskTypeFormOptions: sortBy(
      taskTypeOptions.map((taskType) => ({
        label: mapTaskTypeToLabelV2(taskType),
        value: taskType,
      })),
      "label"
    ),
  };
};

export const useClientsOptions = () => {
  const { enableTaskPersonV2Endpoints } = useFlags();

  const auth = useAdvisorRequestAuth();
  const clientsQuery = useGetTaskClients(
    enableTaskPersonV2Endpoints ? "v2" : "v1",
    auth
  );

  return {
    clientsOptions: useMemo(
      () =>
        orderBy(
          clientsQuery.data?.clients.map(mapPersonToOption) ?? [],
          (entity) => entity.label
        ),

      [clientsQuery.data]
    ),
    clientsQuery,
  };
};

export const useAdvisorsOptions = () => {
  const { enableTaskPersonV2Endpoints } = useFlags();

  const auth = useAdvisorRequestAuth();
  const advisorsQuery = useGetTaskAdvisors(
    enableTaskPersonV2Endpoints ? "v2" : "v1",
    auth
  );

  return {
    advisorsOptions: useMemo(
      () =>
        orderBy(
          advisorsQuery.data?.advisors.map(mapPersonToOption) ?? [],
          (entity) => entity.label
        ),

      [advisorsQuery.data]
    ),
    advisorsQuery,
  };
};

// NOTE: this dictates which assignee type should be listed first
const assigneeTypePriority: AssigneeType[] = [
  AssigneeType.enum.GROUP,
  AssigneeType.enum.PERSON,
];

export const useAssigneesOptions = (
  queryParams?: GetAssigneesQueryParams,
  enableTaskServiceTeamsBeta?: boolean
) => {
  const auth = useAdvisorRequestAuth();
  const nullOrAuth = enableTaskServiceTeamsBeta ? null : auth;
  const authOrNull = enableTaskServiceTeamsBeta ? auth : null;
  const assigneesQueryV1 = useGetTaskAssignees("v1", undefined, nullOrAuth);
  const assigneesQueryV2 = useGetTaskAssignees("v2", queryParams, authOrNull!);

  const assigneesQuery = enableTaskServiceTeamsBeta
    ? assigneesQueryV2
    : assigneesQueryV1;

  const assigneesOptions = useMemo(() => {
    const assignees = assigneesQuery.data?.assignees ?? [];

    return orderBy(assignees, [
      (assignee) => assigneeTypePriority.indexOf(assignee.type),
      (assignee) =>
        isGroupAssignee(assignee)
          ? assignee.groupName
          : assignee.personName.first,
    ]).map(mapAssigneeToOption);
  }, [assigneesQuery.data]);

  return {
    assigneesOptions,
    assigneesQuery,
  };
};

export const useServiceTeamsOptions = () => {
  const { enableTaskServiceTeams } = useFlags();

  const auth = useAdvisorRequestAuth();
  const authOrNull = enableTaskServiceTeams ? auth : null;
  const serviceTeamsQuery = useGetServiceTeams(authOrNull);

  const serviceTeamsOptions = useMemo(() => {
    const serviceTeams = serviceTeamsQuery.data ?? [];

    return orderBy(serviceTeams, [(serviceTeam) => serviceTeam.name]).map(
      mapSerivceTeamToOption
    );
  }, [serviceTeamsQuery.data]);

  return {
    serviceTeamsOptions,
    serviceTeamsQuery,
  };
};

export const usePrefetchDictionaryOptions = () => {
  const { enableTaskServiceTeamsBeta } = useFlags();

  useClientsOptions();
  useServiceTeamsOptions();
  useAdvisorsOptions();
  useAssigneesOptions(undefined, enableTaskServiceTeamsBeta);
};
